import './index.css'

import React, { Component } from 'react'
import Education from '../education'
import Work from '../work'
import Link from '../link'
import Language from '../language'
import Skill from '../skill'
import Course from '../course'
import Project from '../project'

import LabelMessage from '../../../components/utils/label-message'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faPlus } from '@fortawesome/free-solid-svg-icons'

import ModelFactory from './../../../services/model-factory'
import Switch from '../../../components/switch'
import { nanoid } from 'nanoid'
import TextInput from '../../../components/text-input'
import ReactTooltip from 'react-tooltip'

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { sortByOrderOrDate } from '../../../services/sort-sections'

const SORTABLE_MODELS = new Set(['work', 'course', 'project'])

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  }
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {React.cloneElement(children, { dragListeners: listeners })}
    </div>
  )
}

function SortableList({ items, renderItem, onReorder }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex(i => i.key === active.id)
    const newIndex = items.findIndex(i => i.key === over.id)
    onReorder(arrayMove(items, oldIndex, newIndex))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(i => i.key)} strategy={verticalListSortingStrategy}>
        {items.map((element, index) => (
          <SortableItem key={element.key} id={element.key}>
            {renderItem(element, index)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  )
}

class ListComponent extends Component {
  constructor(props) {
    super(props)

    this.state = {
      items: this.props.items,
      showExperience: this.props.showExperience,
    }

    this.modelName = this.props.modelName
    this.header = this.props.header
    this.label = this.props.label

    this.modelFactory = new ModelFactory()
  }

  update = (index, model, triggerSave) => {
    let items = this.state.items
    items[index] = model

    const finalItems = triggerSave ? sortByOrderOrDate(items) : items
    this.setState({ items: finalItems })
    this.props.update(this.modelName, finalItems, triggerSave)
  }

  updateHeaders = (index, model) => {
    this.props.updateHeaders(this.modelName, model)
  }

  addElement = () => {
    const newModel = this.modelFactory.create(this.modelName)
    let items = this.state.items
    items.push(newModel)

    this.setState({ items })

    this.props.update(this.modelName, this.state.items)
  }

  removeElement = index => {
    let items = this.state.items
    items.splice(index, 1)

    this.setState({ items })

    this.props.update(this.modelName, this.state.items)
  }

  handleReorder = reorderedItems => {
    const items = reorderedItems.map((item, index) => ({ ...item, order: index + 1 }))
    this.setState({ items })
    this.props.update(this.modelName, items, true)
  }

  clearSectionOrder = () => {
    const items = sortByOrderOrDate(this.state.items.map(item => ({ ...item, order: null })))
    this.setState({ items })
    this.props.update(this.modelName, items, true)
  }

  updateShowExperience = () => {
    let showExperience = !this.state.showExperience

    let items = this.state.items.map(element => {
      element.key = nanoid()
      return element
    })

    this.setState({ items, showExperience })

    this.props.update('showExperience', showExperience)
    this.props.update('skill', items)
  }

  renderEducation = (element, index) => {
    return (
      <Education
        model={element}
        key={element.key}
        index={index}
        update={this.update}
        removeElement={this.removeElement}
      />
    )
  }

  renderWork = (element, index) => {
    return (
      <Work
        model={element}
        key={element.key}
        index={index}
        update={this.update}
        removeElement={this.removeElement}
      />
    )
  }

  renderLink = (element, index) => {
    return (
      <Link
        model={element}
        key={element.key}
        index={index}
        update={this.update}
        removeElement={this.removeElement}
      />
    )
  }

  renderLanguage = (element, index) => {
    return (
      <Language
        model={element}
        key={element.key}
        index={index}
        update={this.update}
        removeElement={this.removeElement}
      />
    )
  }

  renderSkill = (element, index) => {
    return (
      <Skill
        model={element}
        key={element.key}
        index={index}
        update={this.update}
        removeElement={this.removeElement}
        showExperience={this.state.showExperience}
      />
    )
  }

  renderCourse = (element, index) => {
    return (
      <Course
        model={element}
        key={element.key}
        index={index}
        update={this.update}
        removeElement={this.removeElement}
      />
    )
  }

  renderProject = (element, index) => {
    return (
      <Project
        model={element}
        key={element.key}
        index={index}
        update={this.update}
        removeElement={this.removeElement}
      />
    )
  }

  renderItem = (element, index) => {
    if (this.modelName === 'education') return this.renderEducation(element, index)

    if (this.modelName === 'work') return this.renderWork(element, index)

    if (this.modelName === 'link') return this.renderLink(element, index)

    if (this.modelName === 'language') return this.renderLanguage(element, index)

    if (this.modelName === 'skill') return this.renderSkill(element, index)

    if (this.modelName === 'course') return this.renderCourse(element, index)

    if (this.modelName === 'project') return this.renderProject(element, index)

    return null
  }

  headerPlaceholder = element => {
    const defaultHeaders = {
      summary: 'Summary',
      education: 'Education',
      work: 'Work',
      link: 'Websites & Links',
      language: 'Languages',
      skill: 'Skills',
      course: 'Courses & Trainings',
      project: 'Projects',
    }

    return defaultHeaders[element] || 'Header'
  }

  renderLabel = () => {
    if (this.modelName !== 'skill') return <LabelMessage label={this.label} />

    return (
      <Switch
        label="Don't show experience level"
        value={this.state.showExperience}
        updateDisable={this.updateShowExperience}
      />
    )
  }

  renderItems = () => {
    if (SORTABLE_MODELS.has(this.modelName)) {
      return (
        <SortableList
          items={this.state.items}
          renderItem={this.renderItem}
          onReorder={this.handleReorder}
        />
      )
    }

    return this.props.items.map((element, index) => this.renderItem(element, index))
  }

  render() {
    return (
      <React.Fragment>
        <div className="section-basics-line" style={{ marginBottom: '0px' }}>
          <div className="section-basics-title" title="You can edit this header">
            <div className="pencil-edit" data-tip data-for="{{element}}-pencil-edit">
              <FontAwesomeIcon icon={faPen} className="fa-flip-horizontal" />
              <ReactTooltip id="{{element}}-pencil-edit" type="dark" effect="solid" place="top">
                <p style={{ maxWidth: '200px' }}>You can edit this header.</p>
              </ReactTooltip>
            </div>

            <TextInput
              field={this.modelName}
              value={this.header}
              update={this.updateHeaders}
              placeholder={this.headerPlaceholder(this.modelName)}
              showBorderBottom={false}
            />
          </div>
        </div>

        {this.renderLabel()}

        {this.renderItems()}

        <div className="list-footer">
          <div className="button-add-element" onClick={this.addElement}>
            <div className="button-add-element-icon">
              <FontAwesomeIcon icon={faPlus} />
            </div>
            <span className="button-add-element-span">Add {this.modelName}</span>
          </div>
          {SORTABLE_MODELS.has(this.modelName) && this.state.items.some(i => i.order != null) && (
            <div className="section-clear-order" onClick={this.clearSectionOrder}>
              Sort by date
            </div>
          )}
        </div>
      </React.Fragment>
    )
  }
}

ListComponent.defaultProps = {
  modelName: 'education',
  header: 'Education',
  label: '',
  items: [],
  showExperience: false,
}
export default ListComponent
